<template>
    <div style="display: flex; justify-content: space-between">
        <Title>Terminology</Title>
        <Button
            v-if="isEditing"
            icon="pi pi-plus"
            label="Add term"
            severity="secondary"
            size="small"
            text
            @click="openDomainTermDialog()"
        />
    </div>
    <p class="section-description">
        Shared definitions that keep everyone aligned on what key concepts mean
        within this domain.
    </p>
    <div v-if="!domainTerms?.length" class="empty-state">
        <i class="pi pi-book empty-icon"></i>
        <div>
            <h3>No terminology yet</h3>
            <p>
                <i
                    >Domain terms clarify shared language — concepts everyone in
                    the domain should understand the same way.</i
                >
            </p>
            <Button
                v-if="isEditing"
                icon="pi pi-plus"
                label="Add term"
                severity="primary"
                text
                @click="openDomainTermDialog()"
            />
        </div>
    </div>
    <DataTable
        v-else
        ref="termsTable"
        :value="domainTerms"
        selectionMode="single"
        @row-click="
            (event: DataTableRowClickEvent) => {
                const rowLink = `term-link-${event.data.identity}`;
                termsTable.$el.querySelector(`#${rowLink}`)?.click();
            }
        "
    >
        <Column field="name" header="Name" class="name-col">
            <template #body="{ data, field }">
                <RouterLink
                    v-if="hasValidRouteParams"
                    :id="`term-link-${data.identity}`"
                    class="term-link"
                    :to="getDomainTermRoute(data.identity)"
                >
                    <div>{{ data[field] }}</div>
                </RouterLink>
                <div v-else :id="`term-link-${data.identity}`">
                    {{ data[field] }}
                </div>
            </template>
        </Column>
        <Column field="short_description" header="Description">
            <template #body="slotProps">
                <template v-if="slotProps.data.short_description">
                    {{ slotProps.data.short_description }}
                </template>
                <span
                    v-else
                    v-tooltip.top="'A description is not (yet) available'"
                    >...</span
                >
            </template>
        </Column>
        <Column field="" header="" class="buttons-col">
            <template v-if="isEditing" #body="{ index }">
                <Button
                    v-tooltip.top="'Delete term'"
                    severity="danger"
                    type="button"
                    text
                    rounded
                    icon="pi pi-times"
                    :loading="isLoading"
                    @click="(e: MouseEvent) => deleteDomainTermEntity(e, index)"
                />
            </template>
        </Column>
    </DataTable>
</template>

<script setup lang="ts">
import type { DataTableRowClickEvent } from "primevue/datatable";
import { useConfirm } from "primevue/useconfirm";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";

import Title from "@/components/Title.vue";
import { useEditDomainTermDialog } from "@/composables/edit-domain-term-dialog";
import {
    type DomainTerm,
    useDomainTermsManager,
    useDomainTermsStore,
} from "@/stores/domain-terms-store";

const { isLoading } = useDomainTermsManager();
const { deleteDomainTerm } = useDomainTermsStore();
const termsTable = ref();
const props = defineProps<{
    isEditing: boolean;
    domainTerms: DomainTerm[] | undefined;
}>();

const route = useRoute();
const confirm = useConfirm();

const hasValidRouteParams = computed(() =>
    Boolean(route.params.orgName && route.params.stekName)
);

const getDomainTermRoute = (termId: string) => {
    if (!hasValidRouteParams.value) return undefined;
    return {
        name: "domainTermPage",
        params: {
            orgName: route.params.orgName,
            stekName: route.params.stekName,
            domainTermId: termId,
        },
    };
};

const { open: openDomainTermDialog } = useEditDomainTermDialog();

const deleteDomainTermEntity = (event: Event, index: number) => {
    confirm.require({
        target: event.currentTarget as HTMLElement,
        message: `Delete '${props.domainTerms![index].name}'? This cannot be undone.`,
        icon: "pi pi-exclamation-triangle",
        rejectProps: { label: "Cancel", severity: "secondary", outlined: true },
        acceptProps: { label: "Delete", severity: "danger" },
        accept: async () => {
            await deleteDomainTerm(props.domainTerms![index].identity);
        },
    });
};
</script>

<style scoped lang="scss">
.term-link {
    text-decoration: none;
}

:deep(.p-datatable-thead) > tr > th,
:deep(.p-datatable-tbody) > tr > td {
    &.buttons-col {
        min-width: 50px;
        text-align: right;
    }
    &.name-col {
        width: 30rem;
    }
}
:deep(.p-datatable-tbody) > tr > td.buttons-col {
    padding-top: 0;
    padding-bottom: 0;
}

.section-description {
    margin: 0.25rem 0 1rem;
    color: var(--p-text-muted-color);
    font-size: 0.875rem;
    font-style: italic;
}

.empty-state {
    display: flex;
    flex-direction: column;
    gap: 1em;
    align-items: center;
    text-align: center;

    > * {
        margin: 0;
    }
    .empty-icon {
        font-size: 3rem;
    }
}
</style>
